import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { NotifierService } from 'angular-notifier';
import { firstValueFrom, interval, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ConfigsDto } from 'src/app/domain/models/dto/configs.dto';
import { QuotationProviderDetail } from 'src/app/domain/models/dto/quotation-providers-overview.dto';
import { QuotationStatus } from 'src/app/domain/models/quotation-status.model';
import { ResponseObjectModel } from 'src/app/domain/models/response-object.model';
import { OrdersService } from 'src/app/shared/services/orders.service';

@Component({
  selector: 'app-manage-quotation',
  templateUrl: './manage-quotation.component.html',
  styleUrls: ['./manage-quotation.component.scss'],
})
export class ManageQuotationComponent implements OnInit, OnDestroy {
  private static readonly REFRESH_INTERVAL_MS = 10000;
  private static readonly CONFIGS_SYNC_COOLDOWN_MS = 3000;

  private readonly notifier: NotifierService;
  private readonly destroy$ = new Subject<void>();
  private systemToggleInProgress = false;
  private configsSyncBlockedUntil = 0;
  toggleForm: FormGroup;
  loading: boolean = false;
  loadingReconnect: boolean = false;
  loadingProviders: boolean = false;
  loadingSwitch: boolean = false;
  loadingSystemToggle: boolean = false;
  quotationStatus: 'Conectada' | 'Desconectada' | null = null;
  lastInteraction: string | null = null;
  configs: ConfigsDto = new ConfigsDto();
  currentProviderName: string | null = null;
  currentProviderLastUpdate: string | null = null;
  availableProviders: QuotationProviderDetail[] = [];
  selectedProviderId: string | null = null;

  readonly systemToggles = [
    { controlName: 'backoffice', label: 'Backoffice', apiType: 'backoffice' },
    { controlName: 'partnerHub', label: 'PartnerHub', apiType: 'partnerhub' },
    { controlName: 'massPayment', label: 'Mass Payment', apiType: 'masspayment' },
    { controlName: 'fxTrade', label: 'FxTrade', apiType: 'fxtrade' },
  ];

  constructor(
    private fb: FormBuilder,
    private ordersService: OrdersService,
    notifierService: NotifierService
  ) {
    this.notifier = notifierService;

    this.toggleForm = this.fb.group({
      isOpen: [false],
      backoffice: [false],
      partnerHub: [false],
      fxTrade: [false],
      massPayment: [false],
    });
  }

  ngOnInit() {
    this.refreshData();

    interval(ManageQuotationComponent.REFRESH_INTERVAL_MS)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (
          !this.loadingSwitch &&
          !this.loadingReconnect &&
          !this.systemToggleInProgress &&
          !this.loadingSystemToggle
        ) {
          this.refreshData(true);
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get isConnected(): boolean {
    return this.quotationStatus === 'Conectada';
  }

  refreshData(silent = false) {
    this.loadQuotationStatus(silent);
    this.loadConfigs(silent);
    this.loadProvidersOverview(silent);
  }

  private blockConfigsSync(
    durationMs = ManageQuotationComponent.CONFIGS_SYNC_COOLDOWN_MS
  ) {
    this.configsSyncBlockedUntil = Date.now() + durationMs;
  }

  private canSyncConfigsToForm(): boolean {
    return (
      !this.systemToggleInProgress &&
      !this.loadingSystemToggle &&
      Date.now() >= this.configsSyncBlockedUntil
    );
  }

  private setSystemTogglesLocked(locked: boolean): void {
    this.loadingSystemToggle = locked;
    const options = { emitEvent: false };

    for (const { controlName } of this.systemToggles) {
      const control = this.toggleForm.get(controlName);
      if (!control) {
        continue;
      }

      if (locked) {
        control.disable(options);
      } else {
        control.enable(options);
      }
    }
  }

  private applyConfigsToForm(configs: ConfigsDto, force = false) {
    if (!force && !this.canSyncConfigsToForm()) {
      return;
    }

    const patch = {
      backoffice: configs.backOfficeQuotationEnabled,
      partnerHub: configs.partnerHubQuotationEnabled,
      fxTrade: configs.fxTradeQuotationEnabled,
      massPayment: configs.massPaymentQuotationEnabled,
    };

    const current = this.toggleForm.value;
    const hasChanges =
      current.backoffice !== patch.backoffice ||
      current.partnerHub !== patch.partnerHub ||
      current.fxTrade !== patch.fxTrade ||
      current.massPayment !== patch.massPayment;

    if (!hasChanges) {
      return;
    }

    this.toggleForm.patchValue(patch, { emitEvent: false });
  }

  async loadConfigs(silent = false) {
    try {
      const configs = (await firstValueFrom(this.ordersService.getConfigs()))?.data;
      if (!configs) {
        return;
      }

      this.configs = configs;
      this.applyConfigsToForm(configs);
    } catch {
      if (!silent) {
        this.notifier.notify(
          'error',
          'Erro ao carregar configurações de cotação.'
        );
      }
    }
  }

  async onToggleChange(toggleType: string, event: MatSlideToggleChange) {
    if (this.loadingSystemToggle) {
      event.source.checked = !event.checked;
      return;
    }

    this.blockConfigsSync();
    this.systemToggleInProgress = true;
    this.setSystemTogglesLocked(true);

    try {
      this.configs = (
        await firstValueFrom(
          this.ordersService.toggleQuotation(toggleType, event.checked)
        )
      )?.data;
      this.applyConfigsToForm(this.configs, true);
      this.blockConfigsSync();
    } catch {
      this.applyConfigsToForm(this.configs, true);
      this.notifier.notify('error', 'Erro ao atualizar cotação do sistema.');
    } finally {
      this.systemToggleInProgress = false;
      try {
        await this.loadConfigs(true);
        this.applyConfigsToForm(this.configs, true);
      } finally {
        this.setSystemTogglesLocked(false);
      }
    }
  }

  loadQuotationStatus(silent = false) {
    if (!silent) {
      this.loading = true;
    }
    this.ordersService.getQuotationStatus().subscribe({
      next: (res: ResponseObjectModel<QuotationStatus>) => {
        this.toggleForm.patchValue({
          isOpen: res.data.status == 'ok' ? true : false,
        });
        this.quotationStatus =
          res.data.status == 'ok' ? 'Conectada' : 'Desconectada';
        this.lastInteraction = res.data.timestamp;
        this.loading = false;
      },
      error: (err) => {
        if (!silent) {
          this.notifier.notify('error', 'Erro ao carregar status da cotação.');
        }
        this.loading = false;
      },
    });
  }

  loadProvidersOverview(silent = false) {
    if (!silent) {
      this.loadingProviders = true;
    }
    this.ordersService.getQuotationProvidersOverview().subscribe({
      next: (res) => {
        const overview = res.data?.data;
        if (overview) {
          this.currentProviderName =
            overview.currentProvider?.providerName ?? null;
          this.currentProviderLastUpdate = this.getCurrentProviderLastUpdate(
            overview.currentProvider?.id,
            overview.providers
          );
          this.availableProviders = (overview.providers ?? []).filter(
            (provider) => provider.id !== overview.currentProvider?.id
          );
        }
        this.loadingProviders = false;
      },
      error: () => {
        if (!silent) {
          this.notifier.notify('error', 'Erro ao carregar provedores de cotação.');
        }
        this.loadingProviders = false;
      },
    });
  }

  getCurrentProviderLastUpdate(
    currentProviderId: string | undefined,
    providers: QuotationProviderDetail[]
  ): string | null {
    if (!currentProviderId) {
      return null;
    }

    const currentProvider = providers.find(
      (provider) => provider.id === currentProviderId
    );

    return currentProvider?.lastUpdate ?? null;
  }

  formatDisplayDate(value: string | null): string {
    if (!value) {
      return '—';
    }

    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleString('pt-BR');
  }

  onSwitchProvider() {
    if (!this.selectedProviderId) {
      return;
    }

    this.loadingSwitch = true;
    this.ordersService
      .switchQuotationProvider(this.selectedProviderId)
      .subscribe({
        next: () => {
          this.notifier.notify('success', 'Provedor migrado com sucesso!');
          this.selectedProviderId = null;
          this.loadProvidersOverview();
          this.loadQuotationStatus();
          this.loadingSwitch = false;
        },
        error: () => {
          this.notifier.notify('error', 'Erro ao migrar provedor.');
          this.loadingSwitch = false;
        },
      });
  }

  forceReconnect() {
    this.loadingReconnect = true;
    this.ordersService.forceReconnect().subscribe({
      next: () => {
        this.notifier.notify('success', 'Reconexão forçada com sucesso!');
        this.loadQuotationStatus();
        this.loadingReconnect = false;
      },
      error: () => {
        this.notifier.notify('error', 'Erro ao forçar reconexão.');
        this.loadingReconnect = false;
      },
    });
  }
}
